/**
 * MeneerThreads — conversation storage for the Meneer screen.
 *
 * Conversations are keyed by their own id and each carries a `subjectId`, so a
 * subject holds as many as the student wants, plus a General group whose
 * subjectId is null. Schema v2; v1 held exactly one thread per subject and
 * migrate() converts it.
 *
 * WHY THIS IS ITS OWN MODULE, AND ITS OWN STORAGE KEY. Transcripts are the most
 * quota-hungry thing StudyMeneer will ever store and they grow without a natural
 * end. storage.js warns at 4 MB (_QUOTA_WARN_BYTES) and SKILL 2 is explicit that
 * writing past the browser limit throws and can leave data half-saved. So
 * conversations are capped, pruned on write, and kept away from everything else,
 * because a runaway conversation must never be able to take a student's notes
 * with it.
 *
 * Conversations are deliberately the first thing sacrificed under pressure. A
 * lost conversation is recoverable in a way a lost note is not.
 *
 * TWO CAPS, AND THEY BEHAVE DIFFERENTLY ON PURPOSE. The message cap prunes,
 * because that is what has always shipped; the change is that it now records
 * what it took. The conversation cap REFUSES and deletes nothing — see
 * createConversation.
 *
 * ES module. Exposes window.MeneerThreads (AGENTS.md SKILL 1).
 */

const STORAGE_KEY = 'studymeneer_meneer_threads';

export const SCHEMA_VERSION = 2;

/**
 * Hard cap per conversation. Sized so a long tutoring session survives intact
 * while a year of accumulated chat cannot: 200 messages of ~400 characters is
 * roughly 160 KB, comfortably inside storage.js's 4 MB warning even with a dozen
 * subjects in play.
 */
export const MAX_MESSAGES_PER_CONVERSATION = 200;

/**
 * Conversations per subject. At the cap creation is REFUSED and nothing is
 * deleted. An earlier design pruned the least recently updated conversation,
 * which would have destroyed a transcript a full release before Save-to-note
 * gave the student any way to preserve one. SKILL 2.
 */
export const MAX_CONVERSATIONS_PER_SUBJECT = 20;

/** Subject id for conversations that belong to no subject. */
export const GENERAL_SUBJECT_ID = null;

/**
 * The v1 thread id for the same idea. Kept only so migrate() can recognise it:
 * it was a THREAD id, never a subject id, and carrying it across as a subjectId
 * would file the conversation against a subject that cannot exist.
 */
const V1_GENERAL_THREAD_ID = 'general';

/** Longest a derived title may run before it is truncated. */
const TITLE_MAX = 40;

function _emptyStore() {
    return { version: SCHEMA_VERSION, activeConversationId: null, conversations: {} };
}

/**
 * Date.now() alone collides within a millisecond. subjects.js shipped exactly
 * that and delete() then removed several records at once.
 *
 * Named mintId rather than newId because subjects.js declares a private newId,
 * and tests/cross-module-globals.test.js reads a second declaration of an
 * unpublished name as a binding that vanished when a module was converted.
 */
export function mintId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * A conversation title from the student's first message.
 *
 * Truncated at a word boundary near 40 characters. Cutting mid-word makes a rail
 * of similar questions unreadable, which is the one job the title has. Pure and
 * exported so it is testable without storage.
 */
export function deriveTitle(text) {
    const clean = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
    if (!clean) return 'New chat';
    if (clean.length <= TITLE_MAX) return clean;

    const cut = clean.slice(0, TITLE_MAX + 1);
    const space = cut.lastIndexOf(' ');
    // A single unbroken word longer than the cap has no boundary to cut at, so
    // it is cut hard rather than allowed to overflow the rail.
    const body = space >= 20 ? cut.slice(0, space) : clean.slice(0, TITLE_MAX);
    return body.trim() + '…';
}

/**
 * v1 → v2, in memory.
 *
 * PURE, and exported, because this is the one irreversible thing in the slice
 * and a function that reads storage cannot be tested against the five shapes a
 * real store arrives in. It never touches localStorage; the caller decides
 * whether the result is ever written.
 *
 * Each v1 thread that HAS messages becomes exactly one conversation. Empty
 * threads are dropped: there is nothing in them to preserve, and carrying them
 * would litter the rail on first run. A version above 2 starts empty rather than
 * guessing at a shape this code does not know — the same choice the v1 reader
 * made for an unreadable store.
 */
export function migrate(parsed) {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return _emptyStore();

    if (parsed.version === SCHEMA_VERSION) {
        // Already v2. Returned untouched — re-deriving titles on every read would
        // overwrite a manual rename once per page load.
        if (!parsed.conversations || typeof parsed.conversations !== 'object' ||
            Array.isArray(parsed.conversations)) return _emptyStore();
        return parsed;
    }

    if (typeof parsed.version === 'number' && parsed.version > SCHEMA_VERSION) return _emptyStore();

    const threads = parsed.threads;
    if (!threads || typeof threads !== 'object' || Array.isArray(threads)) return _emptyStore();

    const store = _emptyStore();
    for (const [threadId, thread] of Object.entries(threads)) {
        if (!thread || !Array.isArray(thread.messages) || thread.messages.length === 0) continue;

        const firstUser = thread.messages.find(m => m && m.role === 'user');

        // DERIVED, NOT MINTED. readStore() re-runs this migration on every read
        // until the first v2 write lands, so a random id here means every read
        // invents a different one — and nothing migrated can be addressed twice.
        // getActiveConversationId() returned an id that getConversation() then
        // could not find, and the screen fell back to General on every load
        // while the whole suite passed.
        //
        // The v1 thread id is unique within the store it came from, which is
        // exactly the property needed. The prefix keeps it clear of mintId().
        const id = `v1-${threadId}`;
        store.conversations[id] = {
            id,
            subjectId: String(threadId) === V1_GENERAL_THREAD_ID ? null : String(threadId),
            title: deriveTitle(firstUser ? firstUser.content : ''),
            titleIsAuto: true,
            createdAt: thread.messages[0].at || Date.now(),
            updatedAt: thread.updatedAt || Date.now(),
            messages: thread.messages,
            prunedCount: 0,
        };
    }
    return store;
}

/**
 * Reads the whole store, migrated. Never throws, and NEVER writes.
 *
 * Migration is applied in memory only. The first real write persists v2; until
 * then a v1 store stays intact on disk, so a failed write cannot leave a student
 * with neither shape. A read path that mutates storage is how loadNotes()
 * destroyed a student's only copy of their notes.
 */
export function readStore() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return _emptyStore();
        return migrate(JSON.parse(raw));
    } catch (err) {
        console.error('[MeneerThreads] unreadable store, starting empty:', err);
        return _emptyStore();
    }
}

function _writeAll(store) {
    try {
        store.version = SCHEMA_VERSION;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        return true;
    } catch (err) {
        if (err && err.name === 'QuotaExceededError') {
            if (typeof showNotification === 'function') {
                showNotification('Storage is full. Your conversation could not be saved.', 'error');
            }
        }
        console.error('[MeneerThreads] save failed:', err);
        // The send path calls this. Returning false keeps the conversation alive
        // on screen even though it will not survive a reload, and lets the caller
        // decide what to tell the student. Throwing here would end the
        // conversation instead of reporting a failed save.
        return false;
    }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

/**
 * The one conversation that exists but is not stored.
 *
 * "New chat" creates a conversation in memory and the FIRST MESSAGE persists it.
 * Otherwise every stray click litters the rail with empty conversations the
 * student then has to clean up — and a conversation that never persists removes
 * a whole class of state: it cannot be deleted, renamed, orphaned, or counted
 * against a cap.
 *
 * Module scope, so exactly one can be pending. Creating a second discards the
 * first, which is the correct reading of "navigating away discards it".
 */
let _pending = null;

function _newConversation(subjectId) {
    const now = Date.now();
    return {
        id: mintId(),
        subjectId: subjectId == null ? null : String(subjectId),
        title: 'New chat',
        titleIsAuto: true,
        createdAt: now,
        updatedAt: now,
        messages: [],
        prunedCount: 0,
    };
}

/** Compares ids as strings, treating null and undefined alike. */
function _sameSubject(a, b) {
    return (a == null ? null : String(a)) === (b == null ? null : String(b));
}

function _byRecency(a, b) {
    return (b.updatedAt || 0) - (a.updatedAt || 0);
}

/** Conversations for one subject, most recently updated first. `null` is General. */
export function listConversations(subjectId) {
    return Object.values(readStore().conversations)
        .filter(c => c && _sameSubject(c.subjectId, subjectId))
        .sort(_byRecency);
}

/** Every stored conversation, most recently updated first. Excludes the pending one. */
export function listAllConversations() {
    return Object.values(readStore().conversations).sort(_byRecency);
}

/** The conversation, or null. Includes the pending one, which the screen renders. */
export function getConversation(id) {
    if (!id) return null;
    if (_pending && _pending.id === String(id)) return _pending;
    return readStore().conversations[String(id)] || null;
}

/**
 * Starts a conversation — pending, unstored.
 *
 * AT THE CAP THIS REFUSES AND DELETES NOTHING. The earlier design pruned the
 * least recently updated conversation. This slice is gated only on the message
 * actions, so that would ship a full release before Save-to-note gives a student
 * any way to preserve a transcript before it goes: filling a subject would
 * destroy work with no available escape. Refusing costs one dialog and removes
 * the ordering dependency between the two slices entirely. SKILL 2.
 *
 * @returns {{ok: true, conversation: object} | {ok: false, reason: 'cap', conversations: object[]}}
 */
export function createConversation(subjectId) {
    const existing = listConversations(subjectId);
    if (existing.length >= MAX_CONVERSATIONS_PER_SUBJECT) {
        // The pending slot is left ALONE rather than filled: a refusal that still
        // set _pending would let the very next send persist a conversation past
        // the cap, defeating it one message later.
        //
        // The list travels with the refusal so the caller can name them and offer
        // to open the oldest — the student deletes one, not the app.
        return { ok: false, reason: 'cap', conversations: existing };
    }
    _pending = _newConversation(subjectId);
    return { ok: true, conversation: _pending };
}

/** Throws away the pending conversation. Navigating away calls this. */
export function discardPending() {
    _pending = null;
}

/**
 * Appends one message, persisting a pending conversation on the way.
 *
 * @returns {boolean} false if the write failed — callers must not assume success.
 */
export function appendMessage(conversationId, message) {
    const id = String(conversationId);
    const store = readStore();

    let conv = store.conversations[id];
    if (!conv) {
        if (!_pending || _pending.id !== id) return false;
        conv = _pending;
        store.conversations[id] = conv;
        _pending = null;
    }

    conv.messages.push({
        id: mintId(),
        role: message.role,
        content: message.content,
        at: Date.now(),
        // Receipts of tools that actually ran this turn. Persisted with the
        // message so the record of what Meneer DID survives a reload alongside
        // what it SAID — the two have already disagreed in production.
        ...(message.receipts ? { receipts: message.receipts } : {}),
    });

    // Auto-title from the FIRST thing the student said, and only while the title
    // is still automatic. Tracking the latest message instead would rename the
    // conversation under the student mid-thread, and the rail would stop being a
    // way back to anything.
    if (conv.titleIsAuto && message.role === 'user') {
        const firstUser = conv.messages.find(m => m && m.role === 'user');
        if (firstUser) conv.title = deriveTitle(firstUser.content);
    }

    // Prune on write. Never on read. prunedCount ACCUMULATES rather than being
    // recomputed — the messages it counts are gone, so there is nothing left to
    // recompute from, and a recomputed notice would reset to zero on the next
    // write.
    if (conv.messages.length > MAX_MESSAGES_PER_CONVERSATION) {
        const removed = conv.messages.length - MAX_MESSAGES_PER_CONVERSATION;
        conv.messages = conv.messages.slice(-MAX_MESSAGES_PER_CONVERSATION);
        conv.prunedCount = (conv.prunedCount || 0) + removed;
    }

    conv.updatedAt = Date.now();
    store.activeConversationId = id;
    return _writeAll(store);
}

/**
 * Renames a conversation and stops auto-titling it.
 *
 * @returns {boolean} false if it does not exist or the name is blank — a blank
 *          title leaves an unclickable-looking gap in the rail.
 */
export function renameConversation(id, title) {
    const clean = String(title == null ? '' : title).replace(/\s+/g, ' ').trim();
    if (!clean) return false;

    const store = readStore();
    const conv = store.conversations[String(id)];
    if (!conv) return false;

    conv.title = clean.slice(0, 80);
    conv.titleIsAuto = false;
    conv.updatedAt = Date.now();
    return _writeAll(store);
}

/**
 * Removes one conversation entirely.
 *
 * NOT called when a subject is deleted, deliberately. Orphaning the conversation
 * and surfacing it (orphanedConversations below) keeps the student's writing;
 * cascading would destroy it as a side effect of tidying up a subject, which
 * SKILL 2 forbids. The delete confirmation names the message count instead.
 */
export function deleteConversation(id) {
    const store = readStore();
    if (!store.conversations[String(id)]) return false;
    delete store.conversations[String(id)];
    if (String(store.activeConversationId) === String(id)) store.activeConversationId = null;
    return _writeAll(store);
}

/** Empties a conversation but keeps it, its title and its pruned count. */
export function clearConversation(id) {
    const store = readStore();
    const conv = store.conversations[String(id)];
    if (!conv) return false;
    conv.messages = [];
    conv.updatedAt = Date.now();
    return _writeAll(store);
}

/**
 * The conversation to open, VALIDATED on read.
 *
 * A stored id that no longer resolves — deleted here, or gone after a merge —
 * would render a blank screen with no way back. Falls back to the most recently
 * updated conversation, then to null, which the screen reads as "start fresh".
 * One key, one write, no second source of truth to drift.
 */
export function getActiveConversationId() {
    const store = readStore();
    const id = store.activeConversationId;
    if (id && store.conversations[String(id)]) return String(id);

    const recent = Object.values(store.conversations).sort(_byRecency)[0];
    return recent ? recent.id : null;
}

/**
 * @returns {boolean} false for an id that is not stored — including a pending
 *          conversation, deliberately: the pointer must never name something a
 *          reload cannot find. appendMessage sets it once the write lands.
 */
export function setActiveConversation(id) {
    const store = readStore();
    if (id != null && !store.conversations[String(id)]) return false;
    store.activeConversationId = id == null ? null : String(id);
    return _writeAll(store);
}

// ── Subjects ──────────────────────────────────────────────────────────────────

/**
 * Moves every conversation of one subject onto another. Used when subjects merge.
 *
 * v1 INTERLEAVED the two transcripts by timestamp, because a subject could hold
 * only one conversation and pasting one after the other read as two histories.
 * v2 has no such limit, so the conversations move intact and stay separate —
 * interleaving them now would destroy a boundary the student created.
 *
 * THE CONVERSATION CAP IS NOT ENFORCED HERE, deliberately. It governs creation.
 * Applying it to a merge would make tidying up two subjects delete a transcript,
 * which is exactly what the refusal design exists to prevent. The message cap is
 * likewise untouched: no conversation gains a message.
 *
 * @returns {number} conversations moved.
 */
export function reassignSubject(fromId, toId) {
    if (fromId == null || toId == null || String(fromId) === String(toId)) return 0;

    const store = readStore();
    let moved = 0;
    for (const conv of Object.values(store.conversations)) {
        // `!= null` guards General, whose subjectId is null and which must never
        // be swept into a subject by a merge it has nothing to do with.
        if (conv && conv.subjectId != null && String(conv.subjectId) === String(fromId)) {
            conv.subjectId = String(toId);
            moved++;
        }
    }
    if (moved) _writeAll(store);
    return moved;
}

/**
 * Conversations that still hold messages but whose subject is gone.
 *
 * The rail is built from the CURRENT subject list, so a conversation whose
 * subject was deleted — or whose id changed, which is what recreating a subject
 * does, and what a cloud pull of a different device's subjects does — stops
 * being rendered while still sitting in storage. To the student the conversation
 * is simply gone, and there is nothing anywhere in the app that says otherwise.
 * Present, invisible and unrecoverable is the failure to avoid.
 *
 * Pure: both lists are passed in, so this is testable without a DOM and cannot
 * disagree with the caller about which subjects it read.
 */
export function orphanedConversations(subjects, conversations) {
    const live = new Set((Array.isArray(subjects) ? subjects : [])
        .filter(Boolean)
        // Compared as strings: an id that came back from a JSON round trip as a
        // number must not orphan every conversation that references it.
        .map(s => String(s.id)));

    return (Array.isArray(conversations) ? conversations : []).filter(c =>
        c &&
        c.subjectId != null &&          // General belongs to no subject by design
        !live.has(String(c.subjectId)) &&
        Array.isArray(c.messages) &&
        c.messages.length > 0);
}

export const MeneerThreads = {
    SCHEMA_VERSION,
    MAX_MESSAGES_PER_CONVERSATION,
    MAX_CONVERSATIONS_PER_SUBJECT,
    GENERAL_SUBJECT_ID,
    migrate,
    readStore,
    deriveTitle,
    mintId,
    listConversations,
    listAllConversations,
    getConversation,
    createConversation,
    discardPending,
    appendMessage,
    renameConversation,
    deleteConversation,
    clearConversation,
    getActiveConversationId,
    setActiveConversation,
    reassignSubject,
    orphanedConversations,
};

export default MeneerThreads;

// Transition shim (AGENTS.md SKILL 1). Inline handler attributes resolve against
// the global object, never module scope, so every name one names must stay on
// window until that handler is converted. Count them with
// `node scripts/project-stats.js` — this comment used to state 217, which was
// both stale and the wrong scope (it was the repo-wide markup total, not
// index.html's). ROADMAP_HANDOVER 5.4: do not restate a derivable figure.
if (typeof window !== 'undefined') window.MeneerThreads = MeneerThreads;
